/**
 * Thin Agent module HTTP client for the Connect shell assistant.
 * Domain stays on the agent module; the shell only opens chats with defaults.
 */

import { buildAuthHeaders, getApiOrigin } from '@/auth';

export interface ShellAgent {
  id: number;
  slug?: string | null;
  name?: string | null;
  is_default?: boolean | null;
  active_provider_id?: number | null;
}

export interface ShellConversation {
  id: number;
  agent_id?: number | null;
  title?: string | null;
  context_module_key?: string | null;
  context_resource_type?: string | null;
  context_resource_id?: string | null;
  context_label?: string | null;
}

export interface ShellRecordContext {
  module_key: string;
  resource_type: string;
  resource_id: string;
  label?: string;
}

function agentUrl(path: string): string {
  const base = `${getApiOrigin()}/v1`.replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

async function agentFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = buildAuthHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'BLP-Module': 'agent',
  });
  const res = await fetch(agentUrl(path), {
    ...init,
    headers: {
      ...Object.fromEntries(headers.entries()),
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    let message = `agent ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function listAgents(): Promise<ShellAgent[]> {
  const data = await agentFetch<{ agents?: ShellAgent[] }>('/agents');
  return data.agents ?? [];
}

/**
 * Prefer a usable shell default: marked is_default with a chat provider, else
 * any agent with a provider, else is_default, else first in list.
 * A bare is_default row without active_provider_id is not useful for FABs.
 */
export function pickDefaultAgent(agents: ShellAgent[]): ShellAgent | null {
  if (!agents.length) return null;
  const hasProvider = (a: ShellAgent) =>
    a.active_provider_id != null && Number(a.active_provider_id) > 0;
  const defaults = agents.filter((a) => a.is_default === true);
  const defaultWithProvider = defaults.find(hasProvider);
  if (defaultWithProvider) return defaultWithProvider;
  const anyWithProvider = agents.find(hasProvider);
  if (anyWithProvider) return anyWithProvider;
  if (defaults[0]) return defaults[0];
  return agents[0] ?? null;
}

export async function createConversation(
  agentId: number,
  opts: {
    title?: string;
    context?: ShellRecordContext | null;
  } = {},
): Promise<ShellConversation> {
  const body: Record<string, unknown> = {
    title: opts.title ?? '',
  };
  if (opts.context?.module_key && opts.context.resource_type && opts.context.resource_id) {
    body.context = {
      module_key: opts.context.module_key,
      resource_type: opts.context.resource_type,
      resource_id: opts.context.resource_id,
      ...(opts.context.label ? { label: opts.context.label } : {}),
    };
  }
  const data = await agentFetch<Record<string, unknown>>(
    `/agents/${agentId}/conversations`,
    { method: 'POST', body: JSON.stringify(body) },
  );
  const conv = (data.conversation ?? data.data ?? data) as ShellConversation;
  if (!conv || typeof conv.id !== 'number') {
    throw new Error('invalid conversation response');
  }
  return { ...conv, agent_id: conv.agent_id ?? agentId };
}

/** Non-streaming one-shot chat (fallback when mux is down). */
export async function restChat(
  agentId: number,
  conversationId: number,
  message: string,
): Promise<{ content: string; model?: string; provider?: string }> {
  const data = await agentFetch<{
    content?: string;
    model?: string;
    provider?: string;
  }>(`/agents/${agentId}/chat`, {
    method: 'POST',
    body: JSON.stringify({
      conversation_id: conversationId,
      message,
    }),
  });
  return {
    content: data.content ?? '',
    model: data.model,
    provider: data.provider,
  };
}

export interface TranscribeResult {
  text: string;
  language?: string;
  model?: string;
  provider?: string;
}

/**
 * Upload audio for STT. Prefer WAV/MP3 — FLAC often returns 400 upstream.
 * Field name: file (audio also accepted by the API).
 */
export async function transcribeAudio(
  blob: Blob,
  filename = 'speech.wav',
  opts: { language?: string; model?: string } = {},
): Promise<TranscribeResult> {
  const form = new FormData();
  form.append('file', blob, filename);
  if (opts.language) form.append('language', opts.language);
  if (opts.model) form.append('model', opts.model);

  const headers = buildAuthHeaders({
    Accept: 'application/json',
    'BLP-Module': 'agent',
  });
  // Do not set Content-Type — browser sets multipart boundary.
  headers.delete('Content-Type');

  const res = await fetch(agentUrl('/voice/transcribe'), {
    method: 'POST',
    headers: Object.fromEntries(headers.entries()),
    body: form,
  });
  if (!res.ok) {
    let message = `transcribe ${res.status}`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const data = (await res.json()) as {
    text?: string;
    language?: string;
    model?: string;
    provider?: string;
  };
  return {
    text: (data.text ?? '').trim(),
    language: data.language,
    model: data.model,
    provider: data.provider,
  };
}
